import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SchoolNoticeRichEditor from '../components/schoolNotice/SchoolNoticeRichEditor';
import SchoolNoticeViewModal from '../components/schoolNotice/SchoolNoticeViewModal';
import { PLATFORM_ADMIN_EMAIL } from '../constants/platformAdmin';
import { getSession } from '../services/authService';
import {
  createSchoolNotice,
  deleteSchoolNotice,
  listSchoolNotices,
  updateSchoolNotice,
} from '../services/schoolNoticeService';
import type { SchoolNoticeRow } from '../types/schoolNotice';
import { loadSchoolNoticesCache } from '../utils/platformGlobalAdminCache';

function isAdminSessionEmail(email: string): boolean {
  return email.trim().toLowerCase() === PLATFORM_ADMIN_EMAIL;
}

/** 에듀코디 목록: `2026. 1. 22.` 형식 */
function formatNoticeDateShort(row: SchoolNoticeRow): string {
  const d = new Date(row.updated_at);
  if (Number.isNaN(d.getTime())) return row.updated_at;
  return `${d.getFullYear()}. ${d.getMonth() + 1}. ${d.getDate()}.`;
}

const AdminNoticesPage: React.FC = () => {
  const [sessionEmail, setSessionEmail] = useState('');
  const [list, setList] = useState<SchoolNoticeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isAdmin = useMemo(() => isAdminSessionEmail(sessionEmail), [sessionEmail]);

  useEffect(() => {
    let c = false;
    void getSession()
      .then((s) => {
        if (!c) setSessionEmail((s?.user?.email ?? '').trim().toLowerCase());
      })
      .catch(() => {
        if (!c) setSessionEmail('');
      });
    return () => {
      c = true;
    };
  }, []);

  const loadList = useCallback(async () => {
    const cached = loadSchoolNoticesCache();
    const hasCache = cached.found;
    if (hasCache) {
      // 캐시를 즉시 표시하되, listSchoolNotices의 head(id/updated_at) 비교로 백그라운드 재검증한다.
      setList(cached.rows);
      setLoading(false);
      setLoadError(null);
    } else {
      setLoading(true);
      setLoadError(null);
    }
    try {
      const rows = await listSchoolNotices();
      setList(rows);
    } catch (e) {
      if (!hasCache) {
        setLoadError(e instanceof Error ? e.message : '목록을 불러오지 못했습니다.');
        setList([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    void loadList();
  }, [isAdmin, loadList]);

  const startNew = useCallback(() => {
    setSelectedId(null);
    setDraftTitle('');
    setDraftBody('');
    setSaveError(null);
  }, []);

  const selectRow = useCallback((row: SchoolNoticeRow) => {
    setSelectedId(row.id);
    setDraftTitle(row.title);
    setDraftBody(row.body_html);
    setSaveError(null);
  }, []);

  const cancelDraft = useCallback(() => {
    setSaveError(null);
    if (selectedId) {
      const row = list.find((r) => r.id === selectedId);
      if (row) selectRow(row);
      else startNew();
    } else {
      startNew();
    }
  }, [list, selectedId, selectRow, startNew]);

  const handleSave = async () => {
    if (!isAdmin) return;
    const title = draftTitle.trim();
    if (!title) {
      setSaveError('제목을 입력해 주세요.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (selectedId) {
        const updated = await updateSchoolNotice(selectedId, {
          title,
          body_html: draftBody,
          show_on_login: true,
        });
        setList((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
        setSelectedId(updated.id);
      } else {
        const created = await createSchoolNotice({
          title,
          body_html: draftBody,
          show_on_login: true,
        });
        setList((prev) => [created, ...prev]);
        setSelectedId(created.id);
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRow = async (row: SchoolNoticeRow) => {
    if (!isAdmin) return;
    if (!window.confirm('이 공지를 삭제할까요?')) return;
    setSaving(true);
    setSaveError(null);
    try {
      await deleteSchoolNotice(row.id);
      setList((prev) => prev.filter((r) => r.id !== row.id));
      if (selectedId === row.id) startNew();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-admin-notices-title">
        <h2 id="cp-admin-notices-title" className="cp-page-title">
          공지사항 관리
        </h2>
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자만 사용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section className="cp-admin-notices-page cp-admin-notices-page--edu" aria-labelledby="cp-admin-notices-title">
      <div className="cp-page-title-row cp-admin-notices-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-admin-notices-title">공지사항 관리</h2>
          <p className="cp-admin-legal-subtitle">
            플랫폼 전역 공지입니다. 로그인한 모든 사용자가 조회할 수 있으며, 편집은 플랫폼 관리자만 가능합니다.
          </p>
        </div>
      </div>
      <div className="cp-admin-notices-grid" role="presentation">
        <article className="cp-dashboard-block cp-admin-notices-list-block" aria-labelledby="cp-admin-notices-list-head">
          <h3 className="cp-dashboard-block__head" id="cp-admin-notices-list-head">
            <span className="cp-dashboard-block__head-title">공지사항 목록 관리</span>
          </h3>
          <div className="cp-dashboard-block__body cp-admin-notices-list-body-edu">
            <button type="button" className="cp-admin-notices-create-cta" onClick={startNew} disabled={saving}>
              + 공지사항 만들기
            </button>
            {loading ? (
              <p className="cp-dashboard-placeholder" role="status">
                불러오는 중…
              </p>
            ) : loadError ? (
              <p className="cp-dashboard-placeholder" role="alert">
                {loadError}
              </p>
            ) : list.length === 0 ? (
              <p className="cp-dashboard-placeholder">등록된 공지가 없습니다.</p>
            ) : (
              <ul className="cp-admin-notices-edu-list">
                {list.map((row, index) => (
                  <li key={row.id} className="cp-admin-notices-edu-card" role="none">
                    <div className={'cp-admin-notices-edu-card-inner' + (selectedId === row.id ? ' is-active' : '')}>
                      <button type="button" className="cp-admin-notices-edu-card-main" onClick={() => selectRow(row)} aria-current={selectedId === row.id ? 'true' : undefined}>
                        <span className="cp-admin-notices-edu-card-date">{formatNoticeDateShort(row)}</span>
                        <span className="cp-admin-notices-edu-card-title">{(row.title ?? '').trim() || '(제목 없음)'}</span>
                      </button>
                      <div className="cp-admin-notices-edu-card-tools">
                        {index === 0 ? (
                          <span className="cp-admin-list-applied-badge" title="목록 최상단·로그인 시 안내에 쓰이는 대표 공지">
                            적용 중
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="cp-admin-notices-icon-btn"
                          title="삭제"
                          aria-label="삭제"
                          disabled={saving}
                          onClick={() => void handleDeleteRow(row)}
                        >
                          <img
                            src="/Trash.svg"
                            alt=""
                            className="cp-admin-notices-tool-icon"
                            width={18}
                            height={18}
                            decoding="async"
                          />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </article>
        <article className="cp-dashboard-block cp-admin-notices-editor-block" aria-labelledby="cp-admin-notices-editor-head">
          <h3 className="cp-dashboard-block__head cp-admin-notices-editor-head" id="cp-admin-notices-editor-head">
            <span className="cp-admin-notices-editor-head-spacer" aria-hidden="true" />
            <span className="cp-dashboard-block__head-title">{selectedId ? '공지사항 수정' : '공지사항 작성'}</span>
            <button
              type="button"
              className="cp-admin-notices-preview-btn"
              onClick={() => setPreviewOpen(true)}
              disabled={saving}
            >
              미리보기
            </button>
          </h3>
          <div className="cp-dashboard-block__body cp-admin-notices-editor-body cp-admin-notices-editor-body--edu">
            <div className="cp-admin-notices-title-wrap">
              <input
                id="cp-admin-notice-title"
                type="text"
                className="cp-admin-notices-title-input"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="공지 제목"
                aria-label="공지 제목"
                maxLength={300}
                disabled={saving}
              />
            </div>
            <div className="cp-admin-notices-field cp-admin-notices-field--notice-editor">
              <SchoolNoticeRichEditor
                key={selectedId ?? 'new'}
                value={draftBody}
                onChange={setDraftBody}
                disabled={saving}
                ariaLabel="공지 본문"
              />
            </div>
            {saveError ? (
              <p className="cp-admin-notices-save-error" role="alert">
                {saveError}
              </p>
            ) : null}
            <div className="cp-admin-notices-actions cp-admin-notices-actions--edu">
              <button type="button" className="cp-admin-notices-btn-cancel" onClick={cancelDraft} disabled={saving}>
                취소
              </button>
              <button type="button" className="cp-btn-primary cp-admin-notices-btn-save" onClick={() => void handleSave()} disabled={saving}>
                {saving ? '저장 중…' : '저장'}
              </button>
            </div>
          </div>
        </article>
      </div>
      {previewOpen ? (
        <SchoolNoticeViewModal
          title={draftTitle}
          bodyHtml={draftBody}
          onClose={() => setPreviewOpen(false)}
        />
      ) : null}
    </section>
  );
};

export default AdminNoticesPage;
